import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { FileText, Clock } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { useState } from 'react'

interface HOSLog {
  id: string
  driver_id: string
  driver_name: string
  status: 'off_duty' | 'sleeper' | 'driving' | 'on_duty'
  start_time: number
  end_time: number | null
  duration_minutes: number
  location: string
  notes?: string
}

const statusConfig = {
  off_duty: { label: 'Off Duty', badge: 'secondary' as const },
  sleeper: { label: 'Sleeper', badge: 'secondary' as const },
  driving: { label: 'Driving', badge: 'default' as const },
  on_duty: { label: 'On Duty', badge: 'secondary' as const },
}

export function HOSLogs() {
  const [selectedDriver, setSelectedDriver] = useState<string>('all')
  const [dateRange, setDateRange] = useState<string>('today')

  const { data, isLoading } = useQuery({
    queryKey: ['hos-logs', selectedDriver, dateRange],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (selectedDriver !== 'all') params.set('driver_id', selectedDriver)
      params.set('range', dateRange)

      const res = await fetch(`/api/eld/hos/logs?${params}`)
      if (!res.ok) throw new Error('Failed to fetch HOS logs')
      return res.json() as Promise<{ logs: HOSLog[] }>
    },
    refetchInterval: 60000, // Refresh every minute
  })

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours}h ${mins}m`
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            HOS Activity Logs
          </CardTitle>
          <div className="flex gap-2">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedDriver} onValueChange={setSelectedDriver}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Drivers</SelectItem>
                <SelectItem value="drv_001">John Doe</SelectItem>
                <SelectItem value="drv_002">Jane Smith</SelectItem>
                <SelectItem value="drv_003">Mike Johnson</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading logs...
          </div>
        ) : !data?.logs || data.logs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No logs found for selected filters
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Start Time</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.logs.map((log) => {
                  const config = statusConfig[log.status]
                  const isOngoing = !log.end_time

                  return (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">{log.driver_name}</TableCell>
                      <TableCell>
                        <Badge variant={config.badge} className="gap-1">
                          {isOngoing && <Clock className="h-3 w-3 animate-pulse" />}
                          {config.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(log.start_time), { addSuffix: true })}
                      </TableCell>
                      <TableCell className="text-sm">
                        {isOngoing ? (
                          <span className="text-muted-foreground italic">Ongoing</span>
                        ) : (
                          formatDuration(log.duration_minutes)
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {log.location || 'Unknown'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {log.notes || '-'}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
